import { useMemo, useState } from "react";
import { useTermInterviews } from "../../hooks";
import { InterviewRowActions } from "../interviewList/InterviewRowActions";
import { InterviewStatusBadge } from "../interviewList/InterviewStatusBadge";
import {
  formatInterviewDate,
  formatInterviewTime,
} from "../interviewList/schedule";
import {
  INTERVIEW_STATUS_FILTER_OPTIONS,
  resolveInterviewDisplayStatus,
} from "../interviewList/status";
import { Button } from "../ui/button";

interface TermInterviewListSectionProps {
  termId: string;
  // キャンセルで候補者ステータスも BOOKED → SENT に戻るため、候補者一覧の再取得を親に依頼する
  onInterviewCancelled?: () => void;
}

export function TermInterviewListSection({
  termId,
  onInterviewCancelled,
}: TermInterviewListSectionProps) {
  const { interviews, loading, error, refetch } = useTermInterviews(termId);

  const handleInterviewCancelled = () => {
    refetch();
    onInterviewCancelled?.();
  };
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const visibleInterviews = useMemo(() => {
    const filtered =
      filterStatus === "all"
        ? [...interviews]
        : interviews.filter((interview) => interview.status === filterStatus);
    filtered.sort((a, b) => {
      const timeA = new Date(a.startTime).getTime();
      const timeB = new Date(b.startTime).getTime();
      return sortOrder === "asc" ? timeA - timeB : timeB - timeA;
    });
    return filtered;
  }, [interviews, filterStatus, sortOrder]);

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
          インタビュー一覧
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-9 cursor-pointer rounded-md border border-[var(--color-border-default)] bg-white px-3 text-[13px] text-[var(--color-text-primary)]"
          >
            {INTERVIEW_STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
            }
            className="text-[var(--color-primary-500)]"
          >
            {sortOrder === "desc" ? "新しい順" : "古い順"}
          </Button>
        </div>
      </div>
      <div className="p-6">
        {loading ? (
          <div className="p-8 text-center text-[var(--color-text-secondary)]">
            読み込み中...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-[var(--color-red-600)]">
            インタビュー一覧の読み込みに失敗しました
          </div>
        ) : visibleInterviews.length === 0 ? (
          <div className="p-8 text-center text-[var(--color-text-secondary)]">
            インタビューがありません
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--color-bg-subtle-gray)]">
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    日程
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    候補者
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    ステータス
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    インタビュアー
                  </th>
                  <th className="p-3 text-left font-semibold text-[var(--color-text-secondary)] text-xs">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleInterviews.map((interview) => (
                  <tr
                    key={interview.interviewId}
                    className="border-[var(--color-border-default)] border-b"
                  >
                    <td className="p-3">
                      <div className="font-medium text-[var(--color-text-primary)] text-sm">
                        {formatInterviewDate(interview.startTime)}
                      </div>
                      <div className="text-[var(--color-text-secondary)] text-xs">
                        {formatInterviewTime(interview.startTime)} -{" "}
                        {formatInterviewTime(interview.endTime)}
                      </div>
                    </td>
                    <td className="p-3 text-sm">
                      {interview.candidateName ?? "-"}
                    </td>
                    <td className="p-3">
                      <InterviewStatusBadge
                        status={resolveInterviewDisplayStatus(interview)}
                      />
                    </td>
                    <td className="p-3 text-sm">
                      {interview.interviewerName ||
                        interview.interviewerEmail ||
                        "（名前未設定）"}
                    </td>
                    <td className="p-3">
                      <InterviewRowActions
                        interview={interview}
                        onInterviewCancelled={handleInterviewCancelled}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
